import { useState, useEffect } from "react";
import { startActivityTracking, stopActivityTracking } from "../lib/dailyPlan";
import { useParams, Link } from "react-router-dom";
import { Mic, MicOff, Volume2, Check, X, AlertTriangle, Lightbulb } from "lucide-react";
import lessons from "../data/lessons.json";
import SentenceBuilder from "../components/SentenceBuilder";
import { aiAPI } from "../api";
import { earnXP } from "../gamification";

const VOWELS = ["a", "e", "i", "o", "u"];

// Local fallback grammar check (used when API is unavailable)
function analyzeLocal(input) {
  const errors = [];
  const words = input.trim().split(/\s+/);

  if (input.trim()[0] !== input.trim()[0].toUpperCase()) {
    errors.push({ type: "capitalization", message: "Sentence must start with a capital letter", tip: "Always capitalize the first word.", severity: "error" });
  }
  if (!/[.!?]$/.test(input.trim())) {
    errors.push({ type: "punctuation", message: "Missing punctuation at the end", tip: "End with a period (.), exclamation mark (!), or question mark (?).", severity: "error" });
  }
  for (let i = 0; i < words.length - 1; i++) {
    const word = words[i].toLowerCase();
    const nextWord = words[i + 1].toLowerCase().replace(/[^a-z]/g, "");
    if (!nextWord) continue;
    if (word === "a" && VOWELS.includes(nextWord[0])) {
      errors.push({ type: "article", message: `Wrong article: "a ${words[i + 1]}"`, fix: `an ${words[i + 1]}`, tip: `Use "an" before vowel sounds.`, severity: "error" });
    }
    if (word === "an" && nextWord[0] && !VOWELS.includes(nextWord[0])) {
      errors.push({ type: "article", message: `Wrong article: "an ${words[i + 1]}"`, fix: `a ${words[i + 1]}`, tip: `Use "a" before consonant sounds.`, severity: "error" });
    }
  }
  const score = Math.max(0, 100 - errors.length * 15);
  return { score, errors, suggestions: [], wordCount: words.length, feedback: score >= 90 ? "Excellent!" : score >= 70 ? "Good work!" : "Keep practicing!" };
}

export default function Practice() {
  const { day } = useParams();
  const lesson = lessons.find((l) => l.day === Number(day));
  const [userInput, setUserInput] = useState("");
  const [checked, setChecked] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  // Speech Recognition state
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [speechScore, setSpeechScore] = useState(null);

  // Track time toward daily 1hr written target (Practice has both written + speech, count as written)
  useEffect(() => {
    startActivityTracking("written", "practice");
    return () => stopActivityTracking();
  }, []);
  const [speechRound, setSpeechRound] = useState(1);

  if (!lesson) {
    return (
      <div className="text-center py-10">
        <h2 className="text-2xl font-bold text-gray-800">Lesson not found</h2>
        <Link to="/dashboard" className="text-gold-700 mt-4 inline-block">Go back home</Link>
      </div>
    );
  }

  const { practice } = lesson;

  async function checkSentence() {
    if (!userInput.trim()) return;
    setLoading(true);
    try {
      const data = await aiAPI.grammarCheck(userInput, lesson.topic);
      setAnalysis(data.analysis);
    } catch (err) {
      // Fallback to local analysis
      setAnalysis(analyzeLocal(userInput));
    }
    setChecked(true);
    setLoading(false);
    earnXP(15, "lesson");
  }

  function reset() {
    setUserInput("");
    setChecked(false);
    setAnalysis(null);
  }

  // Speech Recognition
  function startListening() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in your browser. Try Chrome or Edge.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      const spoken = event.results[0][0].transcript;
      const confidence = Math.round(event.results[0][0].confidence * 100);
      setTranscript(spoken);

      // Compare with expected sentence
      const expected = practice.example.toLowerCase().replace(/[.!?,]/g, "").trim();
      const spokenClean = spoken.toLowerCase().replace(/[.!?,]/g, "").trim();

      const expectedWords = expected.split(/\s+/);
      const spokenWords = spokenClean.split(/\s+/);
      let matchCount = 0;
      for (let i = 0; i < expectedWords.length; i++) {
        if (spokenWords[i] === expectedWords[i]) matchCount++;
      }
      const accuracy = Math.round((matchCount / expectedWords.length) * 100);
      const finalScore = Math.round((accuracy * 0.6) + (confidence * 0.4));

      setSpeechScore({ accuracy, confidence, overall: finalScore, spoken, expected: practice.example });
      earnXP(20, "speaking");
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error === "no-speech") {
        alert("No speech detected. Please try again.");
      }
    };

    recognition.onend = () => setIsListening(false);

    recognition.start();
  }

  function nextRound() {
    if (speechRound < 3) {
      setSpeechRound(speechRound + 1);
      setTranscript("");
      setSpeechScore(null);
    }
  }

  function speak(text) {
    if ("speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = Number(localStorage.getItem("speechRate") || "0.85");
      u.lang = "en-US";
      window.speechSynthesis.speak(u);
    }
  }


  return (
    <div className="space-y-4 page-enter relative">
      {/* Decorative blobs */}
      <div className="blob blob-amber" style={{ width: 320, height: 320, top: -40, right: -40, opacity: 0.25 }} />
      <div className="blob blob-purple" style={{ width: 260, height: 260, top: 200, left: -40, opacity: 0.2 }} />

      {/* Header */}
      <div className="relative">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-gold-700 hover:text-gold-700 text-sm font-bold link-animate">&larr; Back to Dashboard</Link>
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <span className="chip-premium" style={{ background: "linear-gradient(135deg, rgba(245, 158, 11, 0.18), rgba(249, 115, 22, 0.18))", color: "#b45309", borderColor: "rgba(245, 158, 11, 0.35)" }}>
            ✍️ Practice
          </span>
          <h1 className="font-display text-3xl md:text-4xl font-normal text-ink-900">
            Day {lesson.day}: <em className="text-gold-600">{lesson.topic}</em>
          </h1>
        </div>
      </div>

      {/* Instructions */}
      <div className="glass-card p-5 relative">
        <h2 className="font-display text-2xl md:text-3xl font-normal text-ink-900 mb-2">Your task.</h2>
        <p className="text-gray-700 dark:text-gray-200 text-base mb-4 leading-relaxed">{practice.prompt}</p>
        <div className="rounded-2xl p-4 bg-gradient-to-br from-gold-100 to-gold-100 dark:from-gold-700/20 dark:to-gold-700/20 border border-gold-300 dark:border-gold-500">
          <p className="text-xs text-gold-700 font-bold uppercase tracking-wider mb-1">Hint</p>
          <p className="text-gold-700 dark:text-gold-300 font-semibold">{practice.hint}</p>
        </div>
      </div>

      {/* Sentence Builder */}
      <SentenceBuilder lesson={lesson} />

      {/* Input Area */}
      <div className="bg-white rounded-2xl shadow-lg p-4">
        <label htmlFor="sentence-input" className="block text-lg font-semibold text-gray-800 mb-3">
          Write your sentence:
        </label>
        <textarea
          id="sentence-input"
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          placeholder="Type your sentence here..."
          className="w-full p-4 border-2 border-gray-200 rounded-xl text-lg focus:border-gold-500 focus:outline-none resize-none"
          rows={3}
          disabled={checked}
        />
        <div className="flex gap-3 mt-4">
          {!checked ? (
            <button
              onClick={checkSentence}
              disabled={!userInput.trim() || loading}
              className="btn-premium btn-premium-primary px-6 py-3 disabled:opacity-50"
            >
              {loading ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-5 h-5" />}
              {loading ? "Analyzing..." : "AI Grammar Check"}
            </button>
          ) : (
            <button onClick={reset} className="btn-premium btn-premium-primary px-6 py-3">
              Try Again
            </button>
          )}
        </div>
      </div>

      {/* AI Feedback */}
      {checked && analysis && (
        <div className="space-y-3">
          {/* Score Banner */}
          <div className={`rounded-2xl shadow-lg p-6 ${
            analysis.score >= 90 ? "bg-sage-light border-2 border-sage-light" :
            analysis.score >= 70 ? "bg-gold-50 border-2 border-gold-300" :
            "bg-berry-light border-2 border-berry-light"
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-bold text-xl ${
                  analysis.score >= 90 ? "text-sage-dark" :
                  analysis.score >= 70 ? "text-gold-700" : "text-berry"
                }`}>
                  {analysis.feedback}
                </p>
                <p className="text-gray-600 text-sm mt-1">{analysis.wordCount} words written</p>
              </div>
              <div className={`text-2xl font-bold ${
                analysis.score >= 90 ? "text-sage-dark" :
                analysis.score >= 70 ? "text-gold-700" : "text-berry"
              }`}>
                {analysis.score}/100
              </div>
            </div>
          </div>

          {/* Errors */}
          {analysis.errors.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-4">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <X className="w-5 h-5 text-berry" /> Issues Found ({analysis.errors.length})
              </h3>
              <div className="space-y-4">
                {analysis.errors.map((err, i) => (
                  <div key={i} className={`rounded-xl p-5 border ${
                    err.severity === "error" ? "bg-berry-light border-berry-light" : "bg-gold-50 border-gold-300"
                  }`}>
                    <div className="flex items-start gap-3">
                      {err.severity === "error" ? <X className="w-5 h-5 text-berry mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-gold-700 mt-0.5" />}
                      <div className="flex-1">
                        <p className={`font-semibold ${err.severity === "error" ? "text-berry-dark" : "text-gold-700"}`}>
                          {err.message}
                        </p>
                        {err.fix && <p className="text-sage-dark mt-1">Correct: <strong>{err.fix}</strong></p>}
                        <div className="bg-white/60 rounded-lg p-3 mt-2">
                          <p className="text-gray-700 text-sm"><strong>Tip:</strong> {err.tip}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggestions */}
          {analysis.suggestions && analysis.suggestions.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-4">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-sky-dark" /> Suggestions
              </h3>
              <div className="space-y-3">
                {analysis.suggestions.map((s, i) => (
                  <div key={i} className="bg-sky-light border border-sky-light rounded-xl p-4">
                    <p className="font-semibold text-sky-dark">{s.message}</p>
                    <p className="text-sky-dark text-sm mt-1">{s.tip}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Example Answer */}
          <div className="bg-white rounded-2xl shadow-lg p-4">
            <h3 className="font-bold text-gray-800 mb-3">Example Answer:</h3>
            <div className="bg-sage-light border border-sage-light rounded-xl p-5">
              <p className="text-sage-dark text-xl font-bold text-center">{practice.example}</p>
            </div>
          </div>

          {/* Common Mistakes */}
          {practice.commonMistakes && (
            <div className="bg-white rounded-2xl shadow-lg p-4">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Common Mistakes to Avoid:</h3>
              <div className="space-y-3">
                {practice.commonMistakes.map((m, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-4 grid md:grid-cols-3 gap-3 items-center">
                    <div>
                      <p className="text-xs font-semibold text-berry uppercase mb-1">Wrong</p>
                      <p className="text-berry line-through">{m.wrong}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-sage-dark uppercase mb-1">Correct</p>
                      <p className="text-sage-dark font-semibold">{m.fix}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gold-700 uppercase mb-1">Why</p>
                      <p className="text-gold-700 text-sm">{m.tip}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Speaking Practice with Speech Recognition */}
      <div className="bg-white rounded-2xl shadow-lg p-4">
        <h2 className="text-xl font-bold text-gray-800 mb-3">Speaking Practice</h2>
        <p className="text-gray-600 mb-4">
          Speak the sentence out loud and get instant feedback on your pronunciation.
        </p>

        {/* Target sentence */}
        <div className="bg-paper-deep rounded-xl p-6 text-center mb-3">
          <p className="text-sm text-gold-700 font-medium mb-2">Say this sentence (Round {speechRound}/3):</p>
          <p className="text-2xl font-bold text-gold-700">"{practice.example}"</p>
          <button onClick={() => speak(practice.example)} className="mt-3 inline-flex items-center gap-2 text-gold-700 hover:text-gold-700 transition-colors">
            <Volume2 className="w-5 h-5" /> Listen first
          </button>
        </div>

        {/* Mic button */}
        <div className="text-center mb-3">
          <button
            onClick={startListening}
            disabled={isListening}
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto transition-all ${
              isListening
                ? "bg-berry text-ink-900 animate-pulse shadow-lg shadow-berry-light"
                : "bg-ink-900 text-ink-900 hover:bg-ink-900 hover:shadow-lg hover:scale-105"
            }`}
          >
            {isListening ? <MicOff className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
          </button>
          <p className="text-sm text-gray-500 mt-3">
            {isListening ? "Listening... Speak now!" : "Tap the mic and speak"}
          </p>
        </div>

        {/* Speech Result */}
        {speechScore && (
          <div className="space-y-4">
            {/* What you said */}
            <div className="bg-gray-50 rounded-xl p-5">
              <p className="text-sm font-semibold text-gray-500 mb-1">You said:</p>
              <p className="text-lg text-gray-800">"{transcript}"</p>
            </div>

            {/* Score breakdown */}
            <div className="grid grid-cols-3 gap-4">
              <div className={`rounded-xl p-4 text-center ${speechScore.accuracy >= 80 ? "bg-sage-light" : speechScore.accuracy >= 50 ? "bg-gold-50" : "bg-berry-light"}`}>
                <p className={`text-2xl font-bold ${speechScore.accuracy >= 80 ? "text-sage-dark" : speechScore.accuracy >= 50 ? "text-gold-700" : "text-berry"}`}>
                  {speechScore.accuracy}%
                </p>
                <p className="text-xs text-gray-500 mt-1">Accuracy</p>
              </div>
              <div className="bg-sky-light rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-sky-dark">{speechScore.confidence}%</p>
                <p className="text-xs text-gray-500 mt-1">Clarity</p>
              </div>
              <div className={`rounded-xl p-4 text-center ${speechScore.overall >= 80 ? "bg-sage-light" : speechScore.overall >= 50 ? "bg-gold-50" : "bg-berry-light"}`}>
                <p className={`text-2xl font-bold ${speechScore.overall >= 80 ? "text-sage-dark" : speechScore.overall >= 50 ? "text-gold-700" : "text-berry"}`}>
                  {speechScore.overall}%
                </p>
                <p className="text-xs text-gray-500 mt-1">Overall</p>
              </div>
            </div>

            {/* Feedback */}
            <div className={`rounded-xl p-4 ${
              speechScore.overall >= 80 ? "bg-sage-light border border-sage-light" :
              speechScore.overall >= 50 ? "bg-gold-50 border border-gold-300" :
              "bg-berry-light border border-berry-light"
            }`}>
              <p className={`font-semibold ${
                speechScore.overall >= 80 ? "text-sage-dark" :
                speechScore.overall >= 50 ? "text-gold-700" : "text-berry"
              }`}>
                {speechScore.overall >= 80 ? "Great pronunciation!" :
                 speechScore.overall >= 50 ? "Good try! Listen again and repeat." :
                 "Keep practicing! Focus on each word clearly."}
              </p>
            </div>

            {/* Next round */}
            {speechRound < 3 && (
              <button onClick={nextRound} className="bg-ink-900 text-ink-900 px-6 py-3 rounded-xl font-semibold hover:bg-ink-900 transition-colors">
                Next Round ({speechRound + 1}/3)
              </button>
            )}
            {speechRound === 3 && speechScore && (
              <div className="bg-paper-deep border border-ink-100 rounded-xl p-4 text-center">
                <p className="text-gold-700 font-bold">Speaking practice complete! Move on to the test.</p>
              </div>
            )}
          </div>
        )}

        {/* Round indicators */}
        {!speechScore && (
          <div className="grid md:grid-cols-3 gap-3">
            <div className={`rounded-xl p-4 text-center ${speechRound === 1 ? "bg-paper-deep border-2 border-gold-500" : "bg-gray-50"}`}>
              <p className="text-sm font-semibold text-gray-500">Round 1</p>
              <p className="text-gray-700 mt-1">Speak slowly, clearly</p>
            </div>
            <div className={`rounded-xl p-4 text-center ${speechRound === 2 ? "bg-paper-deep border-2 border-gold-500" : "bg-gray-50"}`}>
              <p className="text-sm font-semibold text-gray-500">Round 2</p>
              <p className="text-gray-700 mt-1">Normal speed</p>
            </div>
            <div className={`rounded-xl p-4 text-center ${speechRound === 3 ? "bg-paper-deep border-2 border-gold-500" : "bg-gray-50"}`}>
              <p className="text-sm font-semibold text-gray-500">Round 3</p>
              <p className="text-gray-700 mt-1">Fast and confident</p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex gap-4">
        <Link to={`/lesson/${lesson.day}`} className="bg-white text-gold-700 border-2 border-gold-500 px-6 py-3 rounded-xl font-semibold hover:bg-paper-deep transition-colors">
          Review Lesson
        </Link>
        <Link to={`/test/${lesson.day}`} className="bg-sage text-ink-900 px-6 py-3 rounded-xl font-semibold hover:bg-sage transition-colors">
          Take Test
        </Link>
      </div>
    </div>
  );
}
